import React, { useMemo } from 'react';
import { Box, CircleCloseIcon, Inscription, RadioButton, WarningCard, WarningIcon } from '@common-fe/common-fe';

import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { PaymentMethod, PaymentMethodVerificationStatus } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { AllowedPaymentMethodConfig } from '@/modules/user/user.types';

import { PaymentMethodLabel } from '../PaymentMethodLabel';
import { usePaymentMethodHasPendingMessage } from '../PaymentMethodLabel/usePaymentMethodHasPendingMessage';

import { PaymentMethodLayout } from './paymentMethodLayout.types';
import PaymentMethodOptions from './PaymentMethodOptions';

interface Props {
  method: PaymentMethod;
  disableOptions?: boolean;
  removePaymentMethod?: () => void;
  editPaymentMethod?: () => void;
  updateDefaultPaymentMethod?: (paymentMethodId: number) => void; 
  hideIcon?: boolean;
  showPlaidStatus?: boolean;
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  isRejectionFlow?: boolean;
  paymentMethodLayout?: PaymentMethodLayout;
}

export const AddCardPaymentMethod: React.FC<Props> = ({
  method,
  disableOptions,
  removePaymentMethod,
  editPaymentMethod,
  updateDefaultPaymentMethod,
  hideIcon,
  showPlaidStatus,
  allowedPaymentMethodConfigs,
  isRejectionFlow,
  paymentMethodLayout = PaymentMethodLayout.Boxed,
}) => {
  const handleSetChecked = useStore((state) => state.handleSetChecked);
  const handleSetEditableMethodPaymentMethod = useStore(
    (state) => state.handleSetEditableMethodPaymentMethod
  );

  const paymentEmployeeFee = useMemo(
    () => allowedPaymentMethodConfigs?.find((item) => item.paymentMethodType === method?.type)?.paymentEmployeeFee,
    [method, allowedPaymentMethodConfigs]
  );
  const { hasAccountVerificationPendingMessage } = usePaymentMethodHasPendingMessage(method, isRejectionFlow);

  const isVerificationFailed = useMemo(
    () =>
      method?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
      method?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED,
    [method?.verificationStatus]
  );

  const isRadioDisabled = hasAccountVerificationPendingMessage || isVerificationFailed;
  const isDividerLayout = paymentMethodLayout === PaymentMethodLayout.Divider;

  return (
    <Box
      data-testid="AddCardPaymentMethod_container"
      background="canvas"
      border={
        isDividerLayout
          ? [
            { side: 'top', size: '1px', color: 'border2' },
            { side: 'bottom', size: '1px', color: 'border2' },
          ]
          : { size: '1px', color: 'border2' }
      }
      round={isDividerLayout ? undefined : 'container2Round'}
      pad={{ horizontal: 'spacing16', vertical: 'spacing12' }}
      gap="spacing8"
      width={isDividerLayout ? '100%' : undefined}
    >
      <Box direction="row" align="center" justify="between">
        <Box direction="row" align="center" gap="spacing12" flex>
          <Box flex={{ shrink: 0 }}>
            <RadioButton
              name={`${method?.accountId || ''}`}
              data-testid={`radio-button-option-${method?.type}-${method?.id}`}
              key={method?.accountId}
              checked={method.isChecked || false}
              onChange={() => handleSetChecked(method?.id || '')}
              disabled={isRadioDisabled}
            />
          </Box>

          <PaymentMethodLabel
            paymentMethod={method}
            hideIcon={hideIcon}
            showPlaidStatus={showPlaidStatus}
            paymentEmployeeFee={paymentEmployeeFee}
            hasAccountVerificationPendingMessage={hasAccountVerificationPendingMessage}
          />
        </Box>

        {disableOptions ? null : (
          <PaymentMethodOptions
            method={method}
            editPaymentMethod={editPaymentMethod}
            removePaymentMethod={removePaymentMethod}
            updateDefaultPaymentMethod={updateDefaultPaymentMethod}
            handleSetEditableMethodPaymentMethod={handleSetEditableMethodPaymentMethod}
          />
        )}
      </Box>

      {hasAccountVerificationPendingMessage && (
        <WarningCard
          testId="add-card-payment-method_pending"
          icon={<WarningIcon size="24px" />}
          title={
            <Inscription color="textBody" weight="bold" lineHeight="20px">
              You cannot use this payment method yet
            </Inscription>
          }
        >
          <Box margin={{ top: 'spacing4' }}>
            <Inscription color="textBody" lineHeight="20px">
              This payment method is still pending verification. You'll be able to use it as soon as your bank
              account is confirmed.
            </Inscription>
          </Box>
        </WarningCard>
      )}

      {isVerificationFailed && (
        <WarningCard
          testId="add-card-payment-method_verification-failed"
          backgroundColor="dangerContainer"
          borderColor="dangerBorder"
          topBorderColor="danger"
          icon={<CircleCloseIcon size="16px" color="danger" style={{ flexShrink: 0 }} />}
          title={
            <Inscription color="textBody" weight="bold" lineHeight="20px">
              Your bank account could not be verified
            </Inscription>
          }
        >
          <Box margin={{ top: 'spacing4' }}>
            <Inscription color="textBody" lineHeight="20px">
              Update banking account information to try again or add a new bank account.
            </Inscription>
          </Box>
        </WarningCard>
      )}
    </Box>
  );
};
